import { useState } from 'react';
import { Review } from './Review';
import { ComponentConfig } from '@measured/puck';
import {
  MarginProps,
  marginFields,
  marginProps,
} from '../../../page-builder/puck/reusable-props/NewMargin';
import { IAsset } from '../../interface/apiServiceInterface';
import { v4 as uuidv4 } from 'uuid';
import './style.scss';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { Carousel } from 'react-responsive-carousel';
import './../text-editor/style.scss';
import Button from '../../../components/common/button';
import CustomTooltip from '../../../components/common/tooltip';
import { CustomizeProps } from '../../interface/commonInterface';
import TextCustomInput from '../../puck/reusable-props/TextCustomInput';
import { FieldLabel } from '@measured/puck';
import { ReduxSelectorProps } from '../../services/constants';
import {
  ImageProps,
  imageProps,
} from '../../../page-builder/puck/reusable-props/image-props';
import showComponent, {
  showComponentProps,
} from '../../puck/reusable-props/showComponent';
import {
  defaultImage,
  ImageSelector,
} from '../../../components/common/image-selector/ImageSelector';
import {
  getBorderProps,
  getcurrentDeviceType,
  getNewMarginStyles,
  getNumberPattern,
  getPaddingStyles,
  getQuillEditorText,
} from '../../utils/helper';
import { AxiosInstance } from 'axios';
import Border, {
  IBorderValueProps,
  borderProps,
} from '../../../page-builder/puck/reusable-props/Border';
import { paddingFields, PaddingProps } from '../../puck/reusable-props/padding';
import InputField from '../../../components/common/input-field';
import { backgroungFields } from '../../puck/reusable-props/backgroungFields';
import QuillFieldRenderer from './QuillFieldRenderer';

interface ReviewData {
  id: string;
  image: IAsset;
  maxWidth: number;
  height: number;
  name: string;
  review: string;
}

export interface CarouselReviewProps
  extends MarginProps,
    PaddingProps,
    showComponentProps,
    ImageProps {
  reviews: ReviewData[];
  hideImage: boolean;
  dividerLine: boolean;
  interval: number;
  hideVerifiedReview: boolean;
  border: IBorderValueProps;
  starRating: boolean;
  Header: CustomizeProps;
  background: {
    color: string;
    isInherit: boolean;
  };
  isGreenStar: boolean;
}

export interface CustomerProps extends ReduxSelectorProps {
  baseService: AxiosInstance;
}

export const CarouselReview = ({
  baseService,
  useAppSelector,
}: CustomerProps): ComponentConfig<CarouselReviewProps> => {
  return {
    label: (
      <CustomTooltip title="CustomerReviews">
        <span>Carousel Reviews</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      ...showComponent,
      reviews: {
        type: 'custom',
        label: 'Reviews',
        render: ({ value, onChange }) => {
          value = value || [];
          const [openReviewId, setOpenReviewId] = useState<string | null>(null);

          const handleAddReview = () => {
            const newReview: ReviewData = {
              id: uuidv4(),
              name: '<p style="line-height:1.5;"><span style="font-size: 16px;"></span></p>',
              image: defaultImage,
              review:
                '<p style="line-height:1.5;"><span style="font-size: 16px;"></span></p>',
              maxWidth: 100,
              height: 100,
            };
            onChange([newReview, ...value]);
            setOpenReviewId(null);
          };

          const handleEditReview = (
            index: number,
            key: keyof ReviewData,
            newValue: IAsset | string,
          ) => {
            onChange(
              value.map((review, i) =>
                i === index ? { ...review, [key]: newValue } : review,
              ),
            );
          };

          const handleRemoveReview = (index: number) => {
            onChange(value.filter((_: unknown, i: number) => i !== index));
            setOpenReviewId(null);
          };

          const handleToggleReview = (reviewId: string) => {
            setOpenReviewId(openReviewId === reviewId ? null : reviewId);
          };

          return (
            <div>
              <Button
                label="+ Add new review"
                className="mt-4 w-full"
                variant="phoenix"
                onClick={handleAddReview}
              />
              {value.map((review: ReviewData, index: number) => (
                <div key={review.id} className="mt-4">
                  <ImageSelector
                    handleSubmit={selectedImage => {
                      handleEditReview(index, 'image', selectedImage);
                    }}
                    selectedImage={review.image}
                    baseService={baseService}
                  />

                  <QuillFieldRenderer
                    label="Name"
                    value={review.name}
                    onChange={val => handleEditReview(index, 'name', val)}
                    id={index + 1}
                    isOpen={openReviewId === `${review.id}-name`}
                    onToggle={() => handleToggleReview(`${review.id}-name`)}
                  />

                  <QuillFieldRenderer
                    label="Review"
                    value={review.review}
                    onChange={val => handleEditReview(index, 'review', val)}
                    id={index + 1000}
                    isOpen={openReviewId === `${review.id}-review`}
                    onToggle={() => handleToggleReview(`${review.id}-review`)}
                  />

                  <Button
                    label="- Remove Review"
                    className="mt-4 w-full"
                    variant="phoenix_secondary"
                    onClick={() => handleRemoveReview(index)}
                  />
                </div>
              ))}
            </div>
          );
        },
      },
      Header: {
        type: 'custom',
        label: 'Header',
        render: ({ value, onChange, field }) => (
          <TextCustomInput
            value={value}
            onChange={onChange}
            title={field?.label}
            showComponent={true}
            showComponentText="Show Header"
          />
        ),
      },
      ...imageProps([]),
      ...marginFields,
      ...paddingFields,
      border: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <Border value={value} onChange={onChange} title="Border" />
        ),
      },
      ...backgroungFields,
      hideVerifiedReview: {
        type: 'radio',
        label: 'Hide Verified Buyer',
        options: [
          { value: true, label: 'True' },
          { value: false, label: 'False' },
        ],
      },
      isGreenStar: {
        type: 'radio',
        label: 'Star option',
        options: [
          { value: true, label: 'Green Star' },
          { value: false, label: 'Gold Star' },
        ],
      },
      interval: {
        type: 'custom',
        label: 'Gap',
        render: ({ value, onChange }) => (
          <FieldLabel
            label={
              (
                <CustomTooltip title="corousel Interval">
                  <span>Interval</span>
                </CustomTooltip>
              ) as React.ReactNode as string
            }>
            <InputField
              value={value}
              onChange={e => {
                const value = getNumberPattern(e);
                if (value !== null) {
                  onChange(+value);
                }
              }}
            />
          </FieldLabel>
        ),
      },
      starRating: {
        type: 'radio',
        label: 'Star Rating Position',
        options: [
          { value: true, label: 'Top' },
          { value: false, label: 'Bottom' },
        ],
      },
      dividerLine: {
        type: 'radio',
        label: 'Divider Line',
        options: [
          { value: true, label: 'Yes' },
          { value: false, label: 'No' },
        ],
      },
      hideImage: {
        type: 'radio',
        label: 'Hide Image',
        options: [
          { value: true, label: 'True' },
          { value: false, label: 'False' },
        ],
      },
    },
    defaultProps: {
      showComponent: ['desktop', 'tablet', 'mobile'],
      dividerLine: true,
      isGreenStar: false,
      Header: {
        weight: {
          mobile: 'font-normal',
          tablet: 'font-normal',
          desktop: 'font-normal',
        },
        showComponent: {
          mobile: true,
          tablet: true,
          desktop: true,
        },
        deviceType: 'desktop',
        text: 'Trusted Customer Reviews',
        textColor: {
          mobile: '#000000',
          tablet: '#000000',
          desktop: '#000000',
        },
        fontSize: {
          mobile: 'text-sm',
          tablet: 'text-sm',
          desktop: 'text-sm',
        },
      },
      image: {
        height: {
          desktop: '100',
          mobile: '100',
          tablet: '100',
        },
        maxWidth: {
          desktop: '100',
          mobile: '100',
          tablet: '100',
        },
        deviceType: 'desktop',
      },
      reviews: [],
      margin: marginProps.margin,
      padding: {
        deviceType: 'desktop',
        paddingBottom: {
          desktop: '10',
          mobile: '8',
          tablet: '10',
        },
        paddingLeft: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingRight: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingTop: {
          desktop: '10',
          mobile: '8',
          tablet: '10',
        },
      },
      hideImage: false,
      ...borderProps,
      background: {
        color: '#FFFFFF',
        isInherit: false,
      },
      hideVerifiedReview: false,
      starRating: true,
      interval: 5,
    },
    render: ({
      reviews,
      Header,
      margin,
      padding,
      showComponent,
      image,
      hideImage,
      border,
      background,
      hideVerifiedReview,
      dividerLine,
      starRating,
      interval,
      isGreenStar,
      editMode,
    }) => {
      const windowWidth = useAppSelector(state => state.store.currentWidth);
      const currentDeviceType = getcurrentDeviceType(windowWidth, editMode);

      const state = useAppSelector(state => state);
      const newHeader = {
        ...Header,
        text: getQuillEditorText(Header?.text, state),
      };

      return (
        <div
          className={`border-gray-200 text-editor-content ${showComponent?.includes(currentDeviceType) ? '' : '!hidden'}`}
          style={{
            ...getNewMarginStyles(margin, currentDeviceType),
            ...getBorderProps(border, currentDeviceType),
            ...getPaddingStyles(padding, currentDeviceType),
            backgroundColor: background?.isInherit
              ? 'inherit'
              : background?.color,
          }}>
          {newHeader.showComponent?.[currentDeviceType] && (
            <div className="flex items-center justify-center mb-5">
              {dividerLine && (
                <div className="flex-grow border-b border-black mx-2" />
              )}
              <p
                className={`${newHeader.weight?.[currentDeviceType]} ${newHeader.fontSize?.[currentDeviceType]} text-center`}
                style={{ color: newHeader?.textColor?.[currentDeviceType] }}>
                {newHeader?.text}
              </p>
              {dividerLine && (
                <div className="flex-grow border-b border-black mx-2" />
              )}
            </div>
          )}
          <Carousel
            autoPlay={true}
            emulateTouch={true}
            interval={interval * 1000}
            infiniteLoop={true}
            swipeable={true}
            showIndicators={false}
            showStatus={false}
            showThumbs={false}
            className="carousel-wrapper">
            {reviews?.map(r => (
              <Review
                key={r?.id}
                name={getQuillEditorText(r.name, state)}
                review={getQuillEditorText(r.review, state)}
                image={r?.image}
                height={image?.height}
                maxWidth={image?.maxWidth}
                currentDeviceType={currentDeviceType}
                hideImage={hideImage}
                hideVerifiedReview={hideVerifiedReview}
                starRating={starRating}
                isGreenStar={isGreenStar}
              />
            ))}
          </Carousel>
        </div>
      );
    },
  };
};
